import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog as HeadlessDialog } from '@headlessui/react';
import cn from 'classnames';

import type { CommunitySpaceMember } from '~/interfaces/community';

import { POPOVER_PANEL_CLASSES } from '../../ui/_styles/popover';
import { Avatar } from '../../ui/Avatar';
import { Dialog } from '../../ui/Dialog';
import { ModalCloseButton } from '../../ui/ModalCloseButton';

import { MemberFeedShell } from '../../../members/$id/_components/MemberFeedShell';
import { MemberListRowSkeleton, MemberListSkeleton } from '../../../members/$id/_components/MemberListRowSkeleton';

interface SpaceMembersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  spaceName: string;
  countLabel: string;
  members: CommunitySpaceMember[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const SpaceMembersDialog: FC<SpaceMembersDialogProps> = ({
  isOpen,
  onClose,
  spaceName,
  countLabel,
  members,
  ...feed
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className={cn(POPOVER_PANEL_CLASSES, 'relative w-full max-w-md border-0 p-6')}
    >
      <div className="absolute right-4 top-4">
        <ModalCloseButton onClose={onClose} label={t('shared.close')} className="size-8" />
      </div>
      <HeadlessDialog.Title className="pr-8 text-xl font-semibold leading-6 tracking-[-0.4px] text-black">
        {spaceName}
      </HeadlessDialog.Title>
      <p className="mt-1 text-[13px] text-black/50">{countLabel}</p>
      <div className="-mx-2 mt-4 max-h-[min(60vh,420px)] overflow-y-auto px-2">
        <MemberFeedShell
          items={members}
          {...feed}
          renderItem={(member) => {
            const name = member.name || t('community.join.former_member');
            return (
              <div className="flex items-center gap-3 py-2">
                <Avatar src={member.avatar_url} alt={name} className="size-9" />
                <span className="truncate text-[15px] font-medium leading-5 text-black">{name}</span>
              </div>
            );
          }}
          getItemKey={(member) => member.id}
          emptyHeading={countLabel}
          initialSkeleton={<MemberListSkeleton />}
          nextPageSkeleton={<MemberListRowSkeleton />}
          listClassName="flex flex-col"
        />
      </div>
    </Dialog>
  );
};
